import React, { useContext, useEffect, useState } from "react";
import { cloneDeep, set } from "lodash";
import api from "apiSingleton";
import { getJobOrganization } from "utils/helpers/jobOppDetails";
import {
  JOB_OPPORTUNITY_TIME_COMMITMENT,
  EMPLOYMENT_TYPES,
  CURRENCY_CONVERSION_CONFIG,
  TK_SKILL_CATEGORY_MAP,
} from "lookup";
import dayjs from "dayjs";
import { AuthContext, JobsContext, NotesContext } from "context/providers";

import Input from "components/base/Input";
import HelpText from "components/HelpText";
import PrefillPitch from "components/PrefillPitch";
import BullhornMetadata from "components/BullhornMetadata";
import NotesPanel from "components/NotesPanel";
import { getPurifiedParsedMarkDown } from "utils/markdown";
import {
  baseToTargetCurrency,
  loggedUserShouldINRConvert,
} from "utils/helpers/users";
import {
  getBullhornPublishedRates,
  getGroupedSkillsByCategory,
  isBullhornPublishedSalary,
} from "utils/common";

// Display Job Length, if Weeks is present, display weeks,
// else display months
const JobLength = ({ jobOpp }) => {
  if (jobOpp?.jobLengthInWeeks) {
    return (
      <span>
        {jobOpp.jobLengthInWeeks}{" "}
        {jobOpp.jobLengthInWeeks > 1 ? "weeks" : "week"}
      </span>
    );
  }

  if (jobOpp?.jobLength) {
    return (
      <span>
        {jobOpp?.jobLength} {jobOpp?.jobLength > 1 ? "months" : "month"}
      </span>
    );
  }

  return null;
};

const getTalentCompensation = (jobOpp) => {
  if (jobOpp.maxTalentRate?.value != null) {
    return {
      label: "Talent Hourly Rate",
      value: jobOpp.maxTalentRate.value,
    };
  }

  if (jobOpp.maxTalentSalary?.value != null) {
    return {
      label: "Talent Yearly Salary",
      value: jobOpp.maxTalentSalary.value,
    };
  }

  const bullhornRates = getBullhornPublishedRates(jobOpp.metadata);

  if (bullhornRates.high?.value != null) {
    return {
      label: isBullhornPublishedSalary(bullhornRates.high)
        ? "Talent Yearly Salary (BH)"
        : "Talent Rate (BH)",
      value: bullhornRates.high.value,
    };
  }

  return null;
};

const JobOppDetailsCard = ({ user }) => {
  const { loadingJobOpp, jobOpp, updateJob, updateJobLocally } =
    useContext(JobsContext);
  const { user: loggedInUser } = useContext(AuthContext);

  const {
    jobOppNotes,
    job: initJobNotes,
    add: addNote,
    remove: removeNote,
  } = useContext(NotesContext);

  const [inputs, setInputs] = useState({
    torcOwner: {
      value: jobOpp.torcOwner?.username || "",
      successMessage: "",
      errorMessage: "",
      isLoading: false,
      focused: false,
    },
  });

  const getTimeCommitmentLabel = () => {
    if (jobOpp.timeCommitment === JOB_OPPORTUNITY_TIME_COMMITMENT.FULLTIME) {
      return "Full-time";
    }

    return "Part-time";
  };

  const getStartDate = () => {
    const startDate = new Date(jobOpp.startDate);
    if (startDate <= new Date()) {
      return "Immediate";
    }

    const year = startDate.getFullYear();
    const month = (1 + startDate.getMonth()).toString().padStart(2, "0");
    const day = startDate.getDate().toString().padStart(2, "0");

    return month + "/" + day + "/" + year;
  };

  const handleInputChange = ({ value, valueKey }) => {
    setInputs((prev) => ({
      ...prev,
      [valueKey]: {
        ...prev[valueKey],
        errorMessage: "",
        value,
      },
    }));
  };

  const updateInputByPath = (path, value) => {
    setInputs((prev) => {
      const output = cloneDeep(prev);
      const updated = set(output, path, value);

      return updated;
    });
  };

  const addPrimaryMatcher = async () => {
    const shouldUpdate =
      !!inputs.torcOwner.value &&
      inputs.torcOwner.value !== jobOpp.torcOwner?.username;

    let userData;

    if (shouldUpdate) {
      const searchByEmail = inputs.torcOwner.value.includes("@");
      const payloadKey = searchByEmail ? "getUserByEmail" : "getUserByUsername";

      updateInputByPath("torcOwner.isLoading", true);

      try {
        let user;

        if (searchByEmail) {
          user = await api.user.getByEmail({
            email: inputs.torcOwner.value,
          });
        } else {
          user = await api.user.getByUsername({
            username: inputs.torcOwner.value,
          });
        }

        const id = user.data?.[payloadKey]?.items?.[0];

        if (id) {
          userData = user.data?.[payloadKey]?.items?.[0];
        } else {
          updateInputByPath("torcOwner.errorMessage", "User not found");
          updateInputByPath("torcOwner.isLoading", false);

          return;
        }
      } catch (error) {
        updateInputByPath("torcOwner.errorMessage", "User not found");
        updateInputByPath("torcOwner.isLoading", false);
      }

      try {
        const payload = { ...userData };

        await updateJob(jobOpp.id, { torcOwner: { ...payload } });

        updateInputByPath("torcOwner.focused", false);
        updateInputByPath(
          "torcOwner.successMessage",
          "Primary Matcher successfully updated"
        );

        updateJobLocally(payload, "torcOwner");

        setTimeout(() => {
          updateInputByPath("torcOwner.successMessage", "");
        }, 3000);
      } catch (error) {
        const errorMessage =
          error.errors?.[0]?.message || "Something went wrong";

        updateInputByPath("torcOwner.errorMessage", errorMessage);
      } finally {
        updateInputByPath("torcOwner.isLoading", false);
        updateInputByPath("torcOwner.value", userData.username);
      }
    } else {
      updateInputByPath("torcOwner.focused", false);
    }
  };

  const initNotes = async () => {
    try {
      await initJobNotes(jobOpp);
    } catch (error) {
      console.error("initJobNotes error: ", error);
    }
  };

  const createNote = async (note) => {
    try {
      const { content, isPublic } = note;
      const { id: jobOpportunityId } = jobOpp;

      await addNote({ content, isPublic, jobOpportunityId });
      await initNotes();

      return { isSuccess: true };
    } catch (error) {
      console.error("createNote error: ", error);

      return { isSuccess: false };
    }
  };

  const updateFreelancerPitchPrefill = async (field) => {
    try {
      await updateJob(jobOpp.id, { freelancerPitchPrefill: field.value });

      updateJobLocally(field.value, "freelancerPitchPrefill");

      return { isSuccess: true };
    } catch (error) {
      console.error("update error: ", error);

      return { isSuccess: false };
    }
  };

  const handleDeleteNote = async (id) => {
    await removeNote({ id });
    await initNotes();
  };

  useEffect(() => {
    if (jobOpp?.torcOwner?.username) {
      updateInputByPath("torcOwner.value", jobOpp.torcOwner.username);
    }
  }, [jobOpp?.torcOwner?.username]);

  if (loadingJobOpp) {
    return (
      <div className="flex justify-center pt-8">
        <span className="loader"></span>
      </div>
    );
  }

  const talentCompensation = getTalentCompensation(jobOpp);

  const renderSkills = (skills, title, skillsText) => {
    const skillsByCategory = getGroupedSkillsByCategory(skills);

    return (
      <div>
        <h3 className="text-base font-medium text-black tracking-wide font-nexa">
          {title}
        </h3>

        <div className="flex flex-col gap-y-1 p-2">
          {Object.keys(skillsByCategory).map((category) => (
            <div key={category}>
              <h3 className="text-sm  text-black font-medium">
                {TK_SKILL_CATEGORY_MAP[category]}{" "}
                {skillsText && category === "IT" ? `${skillsText}` : skillsText}
              </h3>
              <h4 className="font-rubik text-base italic font-light">
                {skillsByCategory[category]?.map((s) => s.name).join(", ")}
              </h4>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div>
      <div className="flex flex-col md:flex-row">
        <div style={{ flex: `1.7 1 0px` }}>
          <div className="px-8 py-8 w-full">
            <div className="mb-4 grid grid-cols-1 gap-4 pb-2 md:grid-cols-2 md:gap-8">
              <ul className="list-none space-y-1 font-inter text-sm font-medium text-slate-700 [&>li]:relative [&>li]:pl-2 [&>li]:before:absolute [&>li]:before:left-0 [&>li]:before:top-[0.55em] [&>li]:before:h-0.5 [&>li]:before:w-0.5 [&>li]:before:rounded-full [&>li]:before:bg-gray-500">
                <li>
                  {getTimeCommitmentLabel()} | <JobLength jobOpp={jobOpp} />
                </li>
                <li>
                  Customer Name: <span>{getJobOrganization(jobOpp)}</span>
                </li>
                <li>
                  Job Creation Date:{" "}
                  <span>
                    {dayjs(jobOpp.jobCreationDate).format("MM/DD/YYYY") ??
                      "Not specified"}
                  </span>
                </li>
                <li>
                  Start Date: <span>{getStartDate()}</span>
                </li>
                <li>
                  {jobOpp.maxRate ? (
                    <>
                      Hourly Rate:{" "}
                      <span>
                        up to ${jobOpp.maxRate?.value}
                        {loggedUserShouldINRConvert(loggedInUser) &&
                          ` USD (${baseToTargetCurrency(
                            jobOpp.maxRate?.value,
                            CURRENCY_CONVERSION_CONFIG.exchangeFactor
                          )} INR)`}
                      </span>
                    </>
                  ) : (
                    <>
                      Yearly Salary:{" "}
                      <span>
                        up to ${jobOpp.maxSalary?.value}
                        {loggedUserShouldINRConvert(loggedInUser) &&
                          ` USD (${baseToTargetCurrency(
                            jobOpp.maxSalary?.value,
                            CURRENCY_CONVERSION_CONFIG.exchangeFactor
                          )} INR)`}
                      </span>
                    </>
                  )}
                </li>
                {talentCompensation && (
                  <li>
                    {talentCompensation.label}:{" "}
                    <span className="whitespace-nowrap">
                      up to ${talentCompensation.value}
                      {loggedUserShouldINRConvert(loggedInUser) &&
                        ` USD (${baseToTargetCurrency(
                          talentCompensation.value,
                          CURRENCY_CONVERSION_CONFIG.exchangeFactor
                        )} INR)`}
                    </span>
                  </li>
                )}
                <li>
                  Timezone: <span>{jobOpp.timezone?.label}</span>
                </li>
              </ul>

              <ul className="list-none space-y-1 font-inter text-sm font-medium text-slate-700 [&>li]:relative [&>li]:pl-2 [&>li]:before:absolute [&>li]:before:left-0 [&>li]:before:top-[0.55em] [&>li]:before:h-0.5 [&>li]:before:w-0.5 [&>li]:before:rounded-full [&>li]:before:bg-gray-500">
                <li>
                  Timezone Overlap:{" "}
                  <span>
                    {jobOpp.timeOverlap > 1 && jobOpp.timeOverlap < 8
                      ? `${jobOpp.timeOverlap} hours`
                      : jobOpp.timeOverlap >= 8
                      ? "All hours"
                      : "No Restriction"}
                  </span>
                </li>
                {jobOpp.employmentType && (
                  <li>
                    Employment:{" "}
                    <span className="lowercase first-letter:uppercase">
                      {EMPLOYMENT_TYPES[jobOpp.employmentType]}
                    </span>
                  </li>
                )}
                {jobOpp.priorityLevel && (
                  <li>
                    Priority: <span>{jobOpp.priorityLevel}</span>
                  </li>
                )}
                {jobOpp.workMode && (
                  <li>
                    Mode:{" "}
                    <span className="lowercase first-letter:uppercase">
                      {jobOpp.workMode}
                    </span>
                  </li>
                )}
                <li>
                  {inputs.torcOwner.focused ? (
                    <Input
                      className="!flex-row items-center"
                      labelClassName="!my-0 mr-2"
                      inputClassName="border-dashed !p-1 not-italic focus:border-solid text-black font-normal"
                      label="Primary Matcher: "
                      placeholder="Rep username or email"
                      helpText="Internal User working on this job"
                      helpTextPlacement="left"
                      errorMessage={inputs.torcOwner.errorMessage}
                      isLoading={inputs.torcOwner.isLoading}
                      value={inputs.torcOwner.value}
                      valueKey="torcOwner"
                      onChange={handleInputChange}
                      onBlur={addPrimaryMatcher}
                      autoFocus
                    />
                  ) : (
                    <div className="relative flex flex-col">
                      <div className="flex select-none">
                        <span>Primary Matcher:</span>
                        <HelpText
                          text="Internal User working on this job"
                          className="mx-2"
                        />
                        <span
                          title="Double click to edit primary matcher"
                          className="cursor-pointer"
                          onDoubleClick={() =>
                            updateInputByPath("torcOwner.focused", true)
                          }
                        >
                          {inputs.torcOwner.value || "Not specified"}
                        </span>
                      </div>
                      <p className="mt-1 text-sm font-bold text-green-500 transition-all">
                        {inputs.torcOwner.successMessage}
                      </p>
                    </div>
                  )}
                </li>
              </ul>
            </div>
            <NotesPanel
              notes={jobOppNotes[jobOpp.id]}
              loggedInUser={user}
              notesCount={jobOpp.notesCount}
              onSubmit={createNote}
              onDelete={handleDeleteNote}
            />
            <PrefillPitch
              editorLabel="Job questions:"
              className="mt-8"
              field={jobOpp.freelancerPitchPrefill || ""}
              onSubmit={updateFreelancerPitchPrefill}
            />
            <BullhornMetadata
              metadata={jobOpp.metadata}
              showTitle
              className="mt-8"
            />
          </div>
        </div>
        <div style={{ flexBasis: `2px`, alignSelf: `stretch` }} />
        <div className="p-12 flex flex-col gap-8" style={{ flex: `2 1 0px` }}>
          <div className="flex flex-col gap-4">
            <h1 className="font-inter text-2xl font-semibold text-slate-700">
              {jobOpp.title}
            </h1>
            {renderSkills(jobOpp.TKSkills, undefined, "Skills")}
            {jobOpp.optionalTKSkills?.length > 0 &&
              renderSkills(jobOpp.optionalTKSkills, "Optional Skills")}
          </div>
          <p
            className="prose font-inter text-base font-normal text-slate-700"
            dangerouslySetInnerHTML={{
              __html: getPurifiedParsedMarkDown(jobOpp.overview || ""),
            }}
          />
          <h3 className="-mb-6 font-inter text-xl font-semibold text-slate-700">
            Responsibilities
          </h3>
          <p
            className="prose font-inter text-base font-normal text-slate-700"
            dangerouslySetInnerHTML={{
              __html: getPurifiedParsedMarkDown(jobOpp.responsibilities || ""),
            }}
          />
          <h3 className="-mb-6 font-inter text-xl font-semibold text-slate-700">
            Requirements
          </h3>
          <p
            className="prose font-inter text-base font-normal text-slate-700"
            dangerouslySetInnerHTML={{
              __html: getPurifiedParsedMarkDown(jobOpp.requirements || ""),
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default JobOppDetailsCard;
